import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import {
  ContainsAnyOfConditionTypeEnum,
  EqualsConditionTypeEnum,
  GreaterThanConditionTypeEnum,
  GreaterThanOrEqualsConditionTypeEnum,
  IsInConditionTypeEnum,
  IsNullConditionTypeEnum,
  LessThanConditionTypeEnum,
  LessThanOrEqualsConditionTypeEnum,
  LikeConditionTypeEnum,
  MultipleOfConditionTypeEnum,
} from "src/clients/features-retrieve";
import {
  getOperatorDefinition,
  OPERATORS,
  Operator,
} from "src/constants/operators";

/**
 * Backend filter condition types for different field datatypes
 * Uses backend enum types from features-retrieve API
 */
type EventFilterConditionType =
  | EqualsConditionTypeEnum.EQ
  | GreaterThanConditionTypeEnum.GT
  | GreaterThanOrEqualsConditionTypeEnum.GTE
  | LessThanConditionTypeEnum.LT
  | LessThanOrEqualsConditionTypeEnum.LTE
  | LikeConditionTypeEnum.LIKE
  | IsInConditionTypeEnum.IS_IN
  | ContainsAnyOfConditionTypeEnum.CONTAINS_ANY_OF
  | MultipleOfConditionTypeEnum.MULTIPLE_OF
  | IsNullConditionTypeEnum.IS_NULL;

type EventFilterConditionTypeDef = EventFilterConditionType[];

// STRING: eq, contains, starts_with, ends_with, like, is_null
// Note: contains, starts_with, ends_with map to frontend operators that may use "like" with patterns
const stringOperators: EventFilterConditionTypeDef = [
  EqualsConditionTypeEnum.EQ,
  LikeConditionTypeEnum.LIKE,
  IsNullConditionTypeEnum.IS_NULL,
];

// BOOLEAN: eq, is_null
const booleanOperators: EventFilterConditionTypeDef = [
  EqualsConditionTypeEnum.EQ,
  IsNullConditionTypeEnum.IS_NULL,
];

// INTEGER: eq (==), gte (>=), gt (>), lt (<), lte (<=), multiple_of, is_null
const integerOperators: EventFilterConditionTypeDef = [
  EqualsConditionTypeEnum.EQ,
  LessThanConditionTypeEnum.LT,
  LessThanOrEqualsConditionTypeEnum.LTE,
  GreaterThanConditionTypeEnum.GT,
  GreaterThanOrEqualsConditionTypeEnum.GTE,
  MultipleOfConditionTypeEnum.MULTIPLE_OF,
  IsNullConditionTypeEnum.IS_NULL,
];

// ENUM: is_in (one_of), is_null
const enumOperators: EventFilterConditionTypeDef = [
  IsInConditionTypeEnum.IS_IN,
  IsNullConditionTypeEnum.IS_NULL,
];

// TAGS: contains_any_of, is_null
const tagsOperators: EventFilterConditionTypeDef = [
  ContainsAnyOfConditionTypeEnum.CONTAINS_ANY_OF,
  IsNullConditionTypeEnum.IS_NULL,
];

// URL: contains, like, is_null
// Note: contains maps to frontend operator that may use "like" with patterns
const urlOperators: EventFilterConditionTypeDef = [
  LikeConditionTypeEnum.LIKE,
  IsNullConditionTypeEnum.IS_NULL,
];

// RELATION: eq, is_null
const relationOperators: EventFilterConditionTypeDef = [
  EqualsConditionTypeEnum.EQ,
  IsNullConditionTypeEnum.IS_NULL,
];

// NUMBER: same as integer
const numberOperators: EventFilterConditionTypeDef = integerOperators;

// DATE/DATETIME: comparison operators, is_null
const dateOperators: EventFilterConditionTypeDef = [
  EqualsConditionTypeEnum.EQ,
  LessThanConditionTypeEnum.LT,
  LessThanOrEqualsConditionTypeEnum.LTE,
  GreaterThanConditionTypeEnum.GT,
  GreaterThanOrEqualsConditionTypeEnum.GTE,
  IsNullConditionTypeEnum.IS_NULL,
];

const datetimeOperators: EventFilterConditionTypeDef = dateOperators;

/**
 * Gets available backend filter condition types for a given field datatype
 */
export const getAvailableOperators = (
  fieldType: PropertyDefinitionOutputTypeEnum | null,
): EventFilterConditionTypeDef => {
  switch (fieldType) {
    case PropertyDefinitionOutputTypeEnum.STRING:
      return stringOperators;
    case PropertyDefinitionOutputTypeEnum.BOOLEAN:
      return booleanOperators;
    case PropertyDefinitionOutputTypeEnum.INTEGER:
      return integerOperators;
    case PropertyDefinitionOutputTypeEnum.ENUM:
      return enumOperators;
    case PropertyDefinitionOutputTypeEnum.TAGS:
      return tagsOperators;
    case PropertyDefinitionOutputTypeEnum.URL:
      return urlOperators;
    case PropertyDefinitionOutputTypeEnum.RELATION:
      return relationOperators;
    case PropertyDefinitionOutputTypeEnum.NUMBER:
      return numberOperators;
    case PropertyDefinitionOutputTypeEnum.DATE:
      return dateOperators;
    case PropertyDefinitionOutputTypeEnum.DATETIME:
      return datetimeOperators;
    default:
      return [EqualsConditionTypeEnum.EQ];
  }
};

/**
 * Maps backend condition type to frontend operator key
 * Uses frontend operator keys (le, ge) as they are in operators.ts
 */
const translateToOperatorKey = (
  conditionType: EventFilterConditionType,
): Operator | "multiple_of" => {
  switch (conditionType) {
    case EqualsConditionTypeEnum.EQ:
      return "eq";
    case GreaterThanConditionTypeEnum.GT:
      return "gt";
    case GreaterThanOrEqualsConditionTypeEnum.GTE:
      return "ge"; // Map backend "gte" to frontend "ge"
    case LessThanConditionTypeEnum.LT:
      return "lt";
    case LessThanOrEqualsConditionTypeEnum.LTE:
      return "le"; // Map backend "lte" to frontend "le"
    case LikeConditionTypeEnum.LIKE:
      return "like";
    case IsInConditionTypeEnum.IS_IN:
      return "is_in";
    case ContainsAnyOfConditionTypeEnum.CONTAINS_ANY_OF:
      return "contains";
    case MultipleOfConditionTypeEnum.MULTIPLE_OF:
      return "multiple_of";
    case IsNullConditionTypeEnum.IS_NULL:
      return "is_none";
    default:
      return "eq";
  }
};

export const getAvailableOperatorsOptions = (
  datatype: PropertyDefinitionOutputTypeEnum | undefined,
): Array<{
  key: string;
  value: (typeof OPERATORS)[keyof typeof OPERATORS];
}> => {
  if (!datatype) {
    return [];
  }

  // Special handling for STRING: show eq, contains, starts_with, ends_with, like, is_none
  if (datatype === PropertyDefinitionOutputTypeEnum.STRING) {
    return [
      { key: "eq", value: OPERATORS.eq },
      { key: "str_contains", value: getOperatorDefinition("str_contains") },
      { key: "starts_with", value: getOperatorDefinition("starts_with") },
      { key: "ends_with", value: getOperatorDefinition("ends_with") },
      { key: "is_none", value: OPERATORS.is_none },
    ];
  }

  // Special handling for ENUM: show one_of (maps to backend is_in), is_none
  if (datatype === PropertyDefinitionOutputTypeEnum.ENUM) {
    return [
      {
        key: "one_of",
        value: { description: "One of", code: "one of" },
      },
      { key: "is_none", value: OPERATORS.is_none },
    ];
  }

  // Special handling for URL: show contains, like, is_none
  if (datatype === PropertyDefinitionOutputTypeEnum.URL) {
    return [
      { key: "contains", value: OPERATORS.contains },
      { key: "is_none", value: OPERATORS.is_none },
    ];
  }

  const availableOperators = getAvailableOperators(datatype);

  return availableOperators
    .map((conditionType) => {
      const operatorKey = translateToOperatorKey(conditionType);
      if (operatorKey === "multiple_of") {
        return {
          key: "multiple_of",
          value: { description: "Multiple of", code: "multiple of" },
        };
      }
      if (operatorKey in OPERATORS) {
        return {
          key: operatorKey as string,
          value: OPERATORS[operatorKey as keyof typeof OPERATORS],
        };
      }
      return null;
    })
    .filter(
      (
        op,
      ): op is {
        key: string;
        value: (typeof OPERATORS)[keyof typeof OPERATORS];
      } => op !== null,
    );
};
